/**
 * How We Build Slide
 * The homepage's How we build as one slide: what The Home CEO is — AI plus
 * the three kinds of thing every wish adds — and the same wish answered at
 * three levels
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion, useReducedMotion, type TargetAndTransition } from 'framer-motion';
import { ADDS, GROUND, H as STAGE_H, LoopScene, W as STAGE_W, levelTop } from '@components/wish-story/scenes';
import { CUES } from '@components/wish-story/timeline';
import { ENGINE_KINDS, ENGINE_ORDER, EngineIcon } from '@components/wish-story/engines';
import { EASE_OUT_EXPO, FREEZE_FOR_CAPTURE } from '@components/motion/scroll-motion';
import { CANVAS_W, SlideCanvas } from '@components/press/pitch-deck/slide-canvas';
import { CanvasAccent, CanvasEyebrow, CanvasHeading } from '@components/press/pitch-deck/canvas-header';
import { AI_FILL, AI_INK, AiIcon, KIND_NAMES } from '@components/press/pitch-deck/slides/deck-kinds';
import { colors } from '@config/colors';
import { typography } from '@config/typography';

/*
 * On the homepage How we build is a telling in three acts: the first mom's
 * wish and today's generic answer, the night The Home CEO learns from it,
 * the next mom's wish answered in full. A slide gives it in one picture.
 * Across the top, what The Home CEO is, as an equation (June, 2026-09-23:
 * "very clear we're AI + Pre-Fetched Data, Domain Expert Models, and
 * Enhanced Assistant Capabilities") — each kind with what it adds, in the
 * story's own words. Beneath, the story's own summary: the same wish at
 * three levels, each family under its answer.
 *
 * The equation lands term by term, then the levels build as they do at the
 * story's end and the families stay alive. Still (reduced motion, or
 * `?capture=1` — how the deck is exported) it holds the story's own still
 * frame under the whole equation.
 */

/** The loop's three levels are all in place by here: the wish story's own
 *  still frame (`STILL_T` in wish-story.tsx). */
const HOLD_T = CUES.Loop + 6;
/** The build starts so the first level comes in as the equation lands, */
const FROM_T = CUES.Loop - 1.1;
/** a little quicker than the homepage tells it. */
const SPEED = 1.25;

/** The equation's terms: AI, then each kind with what it adds. */
const TERMS: ReadonlyArray<{ key: string; name: string; fill: string; icon: React.ReactNode; adds?: string }> = [
  { key: 'ai', name: 'AI', fill: AI_FILL, icon: <AiIcon size={32} color={AI_INK} /> },
  ...ENGINE_ORDER.map((key) => ({
    key,
    name: KIND_NAMES[key],
    fill: ENGINE_KINDS[key].fill,
    icon: <EngineIcon engine={key} size={30} color={ENGINE_KINDS[key].iconInk} />,
    adds: ADDS[key].add,
  })),
];

/** When each of the equation's terms arrives, in seconds after the slide
 *  does: AI, then each kind with the plus before it. */
const termAt = (i: number) => 0.3 + i * 0.3;

/** The band of the 1600 × 900 stage the levels and the families use — the
 *  cards' tops to the families' shadows — */
const CROP_TOP = 90;
const CROP_BOTTOM = 815;
/** and where it stands on the canvas, under the equation. */
const STAGE_TOP = 420;
const STAGE_HEIGHT = 610;
const S = STAGE_HEIGHT / (CROP_BOTTOM - CROP_TOP);

/** Seconds into the story: from just before the levels, running on so the
 *  families keep moving; held on the still frame, still. */
function useLoopClock(still: boolean): number {
  const [T, setT] = useState(still ? HOLD_T : FROM_T);
  useEffect(() => {
    if (still) {
      setT(HOLD_T);
      return undefined;
    }
    let frame = 0;
    let start = 0;
    const tick = (now: number) => {
      if (!start) start = now;
      setT(FROM_T + ((now - start) / 1000) * SPEED);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [still]);
  return T;
}

export const HowWeBuildSlide: React.FC = () => {
  const still = (useReducedMotion() ?? false) || FREEZE_FOR_CAPTURE;
  const from = (initial: TargetAndTransition): TargetAndTransition | false => (still ? false : initial);
  const T = useLoopClock(still);
  const arrive = (i: number) => ({
    initial: from({ opacity: 0, y: 14 }),
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: EASE_OUT_EXPO, delay: termAt(i) },
  });

  return (
    <SlideCanvas>
      <Header
        initial={from({ opacity: 0, y: 20 })}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE_OUT_EXPO }}
      >
        <CanvasEyebrow>How we build</CanvasEyebrow>
        <CanvasHeading>
          Every wish makes it better for the <CanvasAccent>next mom.</CanvasAccent>
        </CanvasHeading>
      </Header>

      <Equation>
        {TERMS.map((term, i) => {
          const column = 2 * i + 1;
          return (
            <React.Fragment key={term.key}>
              {i > 0 && (
                <Plus {...arrive(i)} style={{ gridColumn: column - 1 }}>
                  <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden="true">
                    <path d="M13 3v20M3 13h20" stroke="currentColor" strokeWidth="3.25" strokeLinecap="round" />
                  </svg>
                  <Hidden>plus</Hidden>
                </Plus>
              )}
              <Disc {...arrive(i)} style={{ gridColumn: column, background: term.fill }}>
                {term.icon}
              </Disc>
              <TermName {...arrive(i)} style={{ gridColumn: column }}>
                {term.name}
              </TermName>
              {term.adds && (
                <TermText {...arrive(i)} style={{ gridColumn: column }}>
                  {term.adds}
                </TermText>
              )}
            </React.Fragment>
          );
        })}
      </Equation>

      <StageWindow>
        <Stage style={{ transform: `translateY(${-CROP_TOP * S}px) scale(${S})` }}>
          <LoopScene T={T} start={CUES.Loop} endAt={Infinity} ground={GROUND} top={levelTop('wide')} />
        </Stage>
      </StageWindow>
    </SlideCanvas>
  );
};

/* Canvas pixels (see SlideCanvas): the header and the equation across the
   top, the levels and their families below. */

const Header = styled(motion.div)`
  position: absolute;
  left: 0;
  right: 0;
  top: 56px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/* AI + the three kinds as one line of names, a plus between each, evenly
   spaced; each term's disc over its name and — for a kind — what it adds
   under it, as wide as the name, so the words never spread the line. */
const Equation = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 204px;
  display: grid;
  grid-template-columns: repeat(${TERMS.length * 2 - 1}, auto);
  grid-template-rows: auto auto auto;
  justify-content: center;
`;

const Disc = styled(motion.div)`
  grid-row: 1;
  justify-self: center;
  width: 68px;
  height: 68px;
  margin-bottom: 14px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 22px ${colors.shadowSoft};
`;

const TermName = styled(motion.div)`
  grid-row: 2;
  justify-self: center;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 30px;
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: -0.01em;
  color: ${colors.neutral.extraDark};
  white-space: nowrap;
`;

/* Sized by its column (the name), never sizing it. */
const TermText = styled(motion.div)`
  grid-row: 3;
  width: 0;
  min-width: 100%;
  margin-top: 8px;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 18px;
  line-height: 1.4;
  text-align: center;
  color: ${colors.neutral.darkMedium};
  text-wrap: balance;
`;

/* The plus between names, in the heading's coral. */
const Plus = styled(motion.div)`
  grid-row: 2;
  align-self: center;
  display: flex;
  padding: 0 34px;
  color: ${colors.accent.coral.dark};
`;

/* Read aloud, not shown: the plus the drawn one stands for. */
const Hidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
`;

const StageWindow = styled.div`
  position: absolute;
  top: ${STAGE_TOP}px;
  left: ${(CANVAS_W - STAGE_W * S) / 2}px;
  width: ${STAGE_W * S}px;
  height: ${STAGE_HEIGHT}px;
  overflow: hidden;
`;

const Stage = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: ${STAGE_W}px;
  height: ${STAGE_H}px;
  transform-origin: 0 0;
`;
